import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import AvatarGroup from '@mui/material/AvatarGroup';
import IconButton from '@mui/material/IconButton';
import AddIcon from '@mui/icons-material/Add';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { Box, Checkbox, Fab, Stack, TextField, Typography } from '@mui/material';
import { FONT_ENUM } from '../../types/global/fonts.types';
import { theme } from '../../constants/theme';
import { useThemeContext } from '../../hook/useThemeManager';
import Cancel from '@mui/icons-material/Cancel';
import { useDispatch, useSelector } from 'react-redux';
import { searchUser } from '../../redux/user/actions';
import _ from 'lodash';
import { IUserSummary } from '../../types/global/helper';
import { ThspianAvatar } from '../Avatar/ThspianAvatar';
import { IHost, IPartner } from '../../redux/discovery/types';
import { userSearchSelector, searchingUserSearchSelector } from '../AvatarGroup/selector';
import { useStyles } from '../AvatarGroup/AvatarGroup.styles';
import { showToastMessage } from '../../utils/AppUtils';
import CustomButton from '../Button/CustomButton';
import { ToolTip } from '../Tip/ToolTip';

export interface IAvatarSelection {
    isEditMode?: boolean;
    title?: string;
    disableTitle?: boolean;
    placeholder?: string;
    value: IUserSummary[];
    onChange?: (selectedUsers: IUserSummary[] | IHost | IPartner[] | undefined) => void;
    isSearchModalOpen?: boolean;
    withProfileSearch?: boolean;
}

/**
 *
 * @param param0
 * @returns
 *
 * I will attempt to handle the api call and redux operation for this module inside here
 * As I see that it only has one functionality, which is to search and select users.
 * In the future we will move it out of here to the parent to keep it uniform.
 */

const SelectTestUsersModal: React.FC<IAvatarSelection> = ({
    isEditMode = true,
    disableTitle,
    placeholder,
    value,
    onChange,
    withProfileSearch,
}) => {
    const [selectedAvatar, setSelectedAvatar] = useState<IUserSummary[]>(value);
    const [initialSelection, setInitialSelection] = useState<IUserSummary[]>(value);
    const [searchValue, setSearchValue] = useState('');
    const [isSearchModalOpen, setSearchModalOpen] = useState(false);
    const { themeStyle, device } = useThemeContext();
    const classes = useStyles();
    const dispatch = useDispatch();
    const userResults = useSelector(userSearchSelector);
    const searchingUser = useSelector(searchingUserSearchSelector);

    const handleGetUsers = useCallback(async () => {
        dispatch(searchUser({ searchTerm: searchValue, withProfileSearch }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useLayoutEffect(() => {
        // This here adds an additional re-render on the app . Check to see if you can move this to appLayout
        handleGetUsers();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedAvatar) {
            setInitialSelection(selectedAvatar);
        }
    }, [selectedAvatar]);

    const handleAddAvatarClick = () => {
        setSearchModalOpen(true);
    };

    const handleModalClose = () => {
        setSearchModalOpen(false);
    };

    const handleSearch = useRef(
        _.debounce(searchTerm => {
            if (searchTerm.length > 3 && !searchingUser) {
                dispatch(searchUser({ searchTerm, withProfileSearch }));
            }
        }, 1000),
    ).current;

    const handleSearchInputChange = (event: any) => {
        const searchValue = event.target.value;
        setSearchValue(searchValue);
        handleSearch(searchValue);
    };

    const handleCheckboxChange = (user: IUserSummary) => {
        const userSummary: IUserSummary = {
            _id: user._id,
            firstName: user.firstName,
            lastName: user.lastName,
            profile_picture: user.profile_picture || '',
            userName: user.userName,
            role: '',
            audme_profile: '',
            id: user._id,
        };

        const isChecked = initialSelection?.some(a => a._id === user._id);

        if (isChecked) {
            const initial = initialSelection.filter(u => u._id !== user._id);
            setInitialSelection(initial);
        } else {
            if (initialSelection.length < 9) {
                setInitialSelection(prevState => [...prevState, userSummary]);
            } else {
                showToastMessage(`Maximum of 10 test users reached`);
            }
        }
    };

    const handleOnChooseTestUsers = () => {
        setSearchModalOpen(false);
        setSelectedAvatar(initialSelection);
        onChange && onChange(initialSelection);
        setInitialSelection([]);
        showToastMessage(`${initialSelection.length} ${initialSelection.length > 1 ? 'users' : 'user'} selected`);
    };

    const handleRemoveUser = (user: IUserSummary) => {
        const filteredUser = selectedAvatar.filter(u => u._id !== user._id);
        const initial = initialSelection.filter(u => u._id !== user._id);
        setSelectedAvatar(filteredUser);
        setInitialSelection(initial);
    };
    return (
        <>
            {!disableTitle && (
                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                    <Typography
                        color={theme[themeStyle].tag_white}
                        sx={{
                            fontFamily: FONT_ENUM.MEDIUM_HEADER,
                            fontSize: 15,
                        }}>
                        Select Test User(s)
                    </Typography>
                    <ToolTip text="Select the users that will receive the test email. If no users are selected, the email will be sent to all users." />
                </Box>
            )}
            <Dialog
                open={isSearchModalOpen}
                onClose={handleModalClose}
                color="blue"
                PaperProps={{
                    style: {
                        backgroundColor: theme[themeStyle].pure_black,
                        height: '50%',
                        width: device.isDesktop ? '30%' : '80%',
                        overflowY: 'scroll',
                        border: '1px solid grey',
                        borderRadius: 10,
                        backdropFilter: 'blur(20px)',
                    },
                }}>
                <TextField
                    autoFocus
                    variant="outlined"
                    placeholder={placeholder ?? `Search and select test users...`}
                    color="secondary"
                    InputProps={{
                        placeholder: placeholder ?? `Search and select test users...`,
                        style: {
                            color: theme[themeStyle].lightWhite, // Change the placeholder color to white
                            border: `1px solid ${theme[themeStyle].lightWhite}`, // Set the border width and color
                            borderRadius: '50px', // Set the border radius
                            paddingLeft: '8px', // Add some padding for the text inside the input field
                            fontSize: 12,
                            minWidth: 200,
                        },
                    }}
                    inputProps={{
                        color: 'blue',
                    }}
                    // Add your search functionality here
                    onChange={searchValue => {
                        handleSearchInputChange(searchValue);
                    }}
                    sx={{
                        padding: 3,
                    }}
                />
                <DialogContent>
                    {userResults && userResults.length > 0 ? (
                        userResults.map((avatar, index) => (
                            <Stack flexDirection="row" alignItems="center" justifyContent="space-between" key={index}>
                                <Stack flexDirection="row" alignItems="center" mb={1.2} gap={0.5}>
                                    <ThspianAvatar
                                        user={avatar as any}
                                        sx={{
                                            width: 50,
                                            height: 50,
                                            cursor: 'pointer',
                                        }}
                                    />
                                    <Typography color={{ color: theme[themeStyle].lightWhite }}>
                                        {avatar.firstName} {avatar.lastName}
                                    </Typography>
                                </Stack>

                                <Checkbox
                                    sx={{
                                        color: theme[themeStyle].lightWhite,
                                    }}
                                    color="secondary"
                                    checked={initialSelection.some(a => a._id === avatar._id)}
                                    onChange={() => handleCheckboxChange(avatar)}
                                />
                            </Stack>
                        ))
                    ) : (
                        <Typography color="white" sx={{ textAlign: 'center', mt: 2 }}>
                            No user found
                        </Typography>
                    )}
                    <CustomButton
                        label="Confirm"
                        disabled={initialSelection.length < 1}
                        onClick={handleOnChooseTestUsers}
                        sx={{
                            position: 'absolute',
                            bottom: 5,
                            left: 10,
                            right: 10,
                        }}
                    />
                </DialogContent>

                <DialogActions sx={{ position: 'absolute', right: 0, top: 0 }}>
                    <IconButton
                        color="primary"
                        onClick={handleModalClose}
                        style={{
                            borderRadius: '50%',
                            borderWidth: 1,
                            borderColor: theme[themeStyle].lightWhite,
                        }}>
                        <Cancel color="secondary" />
                    </IconButton>
                </DialogActions>
            </Dialog>
            <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                <AvatarGroup max={3} variant="circular" className={classes.customAvatarGroup}>
                    {selectedAvatar &&
                        selectedAvatar.map((avatar, index) => (
                            <ThspianAvatar user={avatar} key={index} onClick={() => handleRemoveUser(avatar)} />
                        ))}
                </AvatarGroup>
                {isEditMode && (
                    <Fab size="small" onClick={handleAddAvatarClick}>
                        <AddIcon />
                    </Fab>
                )}
            </Box>
        </>
    );
};

export { SelectTestUsersModal };
